import { Container, Stack } from '@mui/material';
import settings from '/@/core/settings';
import type { useCatalogProductById } from '/@/hooks/catalog';
import type { useFieldById } from '/@/hooks/fields';
import type { useGetUserNode } from '/@/hooks/nodes';
import type {
    useCornDensityCurveForProducts,
    useEnvironments,
    useFertilizationCoefficientsForProducts,
    useYieldByEnvironmentForProducts,
} from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import type { Recommendation } from '/@/types/recommendation';
import { formatDate } from '/@/utils';
import HeroHeader, { pdfHeroLogos } from '../../../components/bayer/hero-header';
import { CornDensityRecommendation } from '.';

const CornDensityRecommendationPDF = ({
    recommendation,
    language,
    unitProviderProps,
}: {
    recommendation: Recommendation;
    language: string;
    unitProviderProps: any;
}) => {
    const cornDensityRecommendationProps: {
        id: number;
        productId: number;
        plantingDate: string;
        soilMoisture: number;
        climatePattern: number;
        form: undefined;
        updatedAt: string;
        environment: number;
        userNitrogen: number;
        userDensity: number;
        successRate: number;

        field: ReturnType<typeof useFieldById>['data'];
        product: ReturnType<typeof useCatalogProductById>['data'];
        environments: ReturnType<typeof useEnvironments>['data'];
        cornDensityCurve: ReturnType<typeof useCornDensityCurveForProducts>['data'];
        cornDensityCurveQueryIsLoading: boolean;
        fertilizationCoefficients: ReturnType<typeof useFertilizationCoefficientsForProducts>['data'];
        yieldByEnvironment: ReturnType<typeof useYieldByEnvironmentForProducts>['data'];
        yieldByEnvironmentQueryIsLoading: boolean;
        userNode: ReturnType<typeof useGetUserNode>['data'];
    } = {
        productId: recommendation.cornDensity?.productProfile || 0,

        // Other Data
        field: recommendation.otherData.field,
        product: recommendation.otherData.product,
        environments: recommendation.otherData.environments,
        cornDensityCurve: recommendation.otherData.cornDensityCurve,
        fertilizationCoefficients: recommendation.otherData.fertilizationCoefficients,
        yieldByEnvironment: recommendation.otherData.yieldByEnvironment,
        userNode: recommendation.otherData.userNode,

        cornDensityCurveQueryIsLoading: false,
        yieldByEnvironmentQueryIsLoading: false,

        form: undefined,

        // Recommendation
        plantingDate: recommendation.cornDensity?.plantingDate || '',
        soilMoisture: recommendation.cornDensity?.soilMoisture || 0,
        climatePattern: recommendation.cornDensity?.climatePattern || 0,
        updatedAt: recommendation.updated,
        id: recommendation.id,
        environment: recommendation.cornDensity?.environment || 0,
        userNitrogen: recommendation.cornDensity?.userNitrogen || 0,
        userDensity: recommendation.cornDensity?.userDensity || 0,
        successRate: recommendation.cornDensity?.userPlanterEfficiency || 0,
    };

    return (
        <Stack spacing={4}>
            <HeroHeader
                title={_('Recomendación de densidad de {{hybridName}}', {
                    hybridName: cornDensityRecommendationProps.product?.name,
                })}
                body={_(
                    'Calculamos la cantidad de semillas y las bolsas necesarias según el híbrido. Última actualización {{updatedAt}}',
                    {
                        updatedAt: formatDate(new Date(cornDensityRecommendationProps.updatedAt)),
                    }
                )}
                background={'expertobayer'}
                maxWidth='lg'
                actions={
                    <Stack direction='row' spacing={2}>
                        <img
                            src={cornDensityRecommendationProps.product.brand.logo}
                            alt={cornDensityRecommendationProps.product.brand.name}
                            height='40px'
                        />
                        {settings.app.theme === 'expertobayer' && (
                            <>
                                <img src={pdfHeroLogos.expertobayer} alt={'Experto Bayer'} height='40px' />
                                <img src={pdfHeroLogos.bayer} alt={'Bayer'} height='40px' />
                            </>
                        )}
                    </Stack>
                }
            />
            <Container maxWidth='lg'>
                <CornDensityRecommendation
                    isView={true}
                    isPdf={true}
                    {...cornDensityRecommendationProps}
                    unitProviderProps={unitProviderProps}
                />
            </Container>
        </Stack>
    );
};

export default CornDensityRecommendationPDF;
